import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { DatePicker } from '../../components/ui/DatePicker';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { SelectField } from '../../components/ui/SelectField';
import { Spinner } from '../../components/ui/Spinner';
import { Textarea } from '../../components/ui/textarea';
import { ApiError } from '../../lib/api';
import { buildPatch, formFromEntry, isDirty, type InspirationEditForm } from '../../lib/portalInspiration/editForm';
import { ADAPT_FOR_OPTIONS } from '../../lib/portalInspiration/adaptFor';
import { INSPIRATION_DESCRIPTION_MAX, INSPIRATION_TREND_LINE_MAX, INSPIRATION_HOWTO_MAX, INSPIRATION_TITLE_MAX } from '../../lib/portalInspiration/entryRules';
import {
  inspirationAccessSentence,
  inspirationReachSentence,
  publishBlocker,
  rowLabel,
  statusLabel,
} from '../../lib/portalInspiration/staffView';
import { INSPIRATION_USAGES, INSPIRATION_USAGE_LABELS, type InspirationAdaptFor, type InspirationUsage, type StaffInspirationRow } from '../../types/portalInspiration';
import type { Account } from '../../types/account';
import { TagChipInput } from '../portalAssets/TagChipInput';
import { AudienceRulePicker } from '../portalLibrary/AudienceEditor';
import { listAccounts } from '../accounts';
import {
  deleteInspiration,
  previewInspirationReach,
  publishInspiration,
  unpublishInspiration,
  updateInspiration,
} from './api';
import { draftEntry } from './draftClient';

// The staff edit dialog for one inspiration entry: media preview, the words, usage, tags,
// audience (the library's own picker), the schedule, AI draft, publish / unpublish / delete.

const USAGE_OPTIONS = INSPIRATION_USAGES.map((u) => ({ value: u, label: INSPIRATION_USAGE_LABELS[u] }));

export function InspirationEditDialog({
  row,
  localFile,
  onClose,
}: {
  row: StaffInspirationRow;
  /** The File just uploaded in this tab, when there is one: the best source of frames for the AI. */
  localFile: Blob | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { entry, asset } = row;
  const initial = useMemo(() => formFromEntry(entry), [entry]);
  const [form, setForm] = useState<InspirationEditForm>(initial);
  const [note, setNote] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [aiMessage, setAiMessage] = useState<string | null>(null);
  const published = entry.status === 'published';

  // Reload the form when the server's copy changes (after a save or an AI draft).
  useEffect(() => setForm(initial), [initial]);

  const accounts = useQuery({ queryKey: ['accounts'], queryFn: listAccounts });
  const reach = useQuery({
    queryKey: ['portal-inspiration-reach', JSON.stringify(form.audience)],
    queryFn: () => previewInspirationReach(form.audience),
    enabled: form.audience.length > 0,
  });

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['portal-inspiration'] });
  const fail = (err: unknown) => toast.error(err instanceof ApiError || err instanceof Error ? err.message : 'Something went wrong.');
  const set = (p: Partial<InspirationEditForm>) => setForm((f) => ({ ...f, ...p }));
  const dirty = isDirty(initial, form);

  const save = useMutation({
    mutationFn: () => updateInspiration(entry.id, buildPatch(initial, form)),
    onSuccess: () => {
      toast.success('Saved.');
      invalidate();
    },
    onError: fail,
  });

  const publish = useMutation({
    mutationFn: async () => {
      if (dirty) await updateInspiration(entry.id, buildPatch(initial, form));
      return publishInspiration(entry.id);
    },
    onSuccess: () => {
      toast.success('Published. Clients in the audience can see it now.');
      invalidate();
      onClose();
    },
    onError: fail,
  });

  const unpublish = useMutation({
    mutationFn: () => unpublishInspiration(entry.id),
    onSuccess: () => {
      toast.success('Unpublished — it is a draft again.');
      invalidate();
    },
    onError: fail,
  });

  const remove = useMutation({
    mutationFn: () => deleteInspiration(entry.id),
    onSuccess: () => {
      toast.success('Deleted.');
      invalidate();
      onClose();
    },
    onError: fail,
  });

  // ⚠️ ON-DEMAND ONLY: this runs from the button below and nowhere else (draftOnDemandOnly.test.ts).
  const draft = useMutation({
    mutationFn: async () => {
      // Save unsaved edits first so the server's no-overwrite rule sees them as the person's own.
      if (dirty) await updateInspiration(entry.id, buildPatch(initial, form));
      return draftEntry({ entryId: entry.id, mediaType: entry.mediaType, asset, file: localFile, note });
    },
    onSuccess: (r) => {
      setAiMessage(
        `AI saw ${r.framesSeen} frame${r.framesSeen === 1 ? '' : 's'}.` +
          (r.written.length > 0 ? ` Wrote: ${r.written.join(', ')}.` : ' Nothing was written.') +
          (r.kept.length > 0 ? ` Kept your edits to: ${r.kept.join(', ')}.` : ''),
      );
      invalidate();
    },
    onError: fail,
  });

  const blocker = publishBlocker({ ...row, entry: { ...entry, ...patchPreview(form) }, reach: reach.data?.reach ?? row.reach });
  const st = statusLabel(row);
  const busy = save.isPending || publish.isPending || unpublish.isPending || remove.isPending || draft.isPending;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {rowLabel(row)}
            <Badge variant={st.tone === 'ok' ? 'success' : st.tone === 'warn' ? 'warning' : st.tone === 'bad' ? 'danger' : 'neutral'}>{st.text}</Badge>
          </DialogTitle>
          <DialogDescription>
            {entry.mediaType === 'video' ? 'Video' : 'Photo'} · {asset?.originalFilename ?? 'file missing'}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-5 sm:grid-cols-[220px_1fr]">
          <div>
            <MediaPreview row={row} />
            {entry.ai && (
              <p className="mt-2 text-xs text-slate-500">
                AI drafted this ({entry.ai.framesSeen} frame{entry.ai.framesSeen === 1 ? '' : 's'} seen)
                {entry.ai.fields.length > 0 ? ` · still AI-written: ${entry.ai.fields.join(', ')}` : ' · all since edited'}
              </p>
            )}
          </div>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="insp-title">Title</Label>
              <Input id="insp-title" value={form.title} maxLength={INSPIRATION_TITLE_MAX + 40} onChange={(e) => set({ title: e.target.value })} />
              <p className="text-xs text-slate-500">
                {entry.mediaType === 'image' ? 'A short “Format — where” caption, e.g. Post-it — held to camera, club behind.' : 'A punchy name, e.g. The Wipe Reveal.'}
              </p>
            </div>
            {entry.mediaType === 'video' && (
              <div className="space-y-1.5">
                <Label htmlFor="insp-trendLine">Trend line</Label>
                <Input id="insp-trendLine" value={form.trendLine} maxLength={INSPIRATION_TREND_LINE_MAX + 40} onChange={(e) => set({ trendLine: e.target.value })} />
                <p className="text-xs text-slate-500">
                  {form.adaptFor === 'none'
                    ? 'The original spoken or on-screen line.'
                    : 'One line on what happens in the original trend and why it works.'}
                </p>
              </div>
            )}
            {form.adaptFor !== 'none' && (
              <div className="space-y-1.5">
                <Label htmlFor="insp-lines">Suggested lines</Label>
                <Textarea id="insp-lines" rows={3} value={form.suggestedLines} onChange={(e) => set({ suggestedLines: e.target.value })} />
                <p className="text-xs text-slate-500">1–3 replacement on-screen lines for this kind of business, one per line.</p>
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="insp-desc">Description</Label>
              <Textarea id="insp-desc" rows={4} value={form.description} maxLength={INSPIRATION_DESCRIPTION_MAX + 200} onChange={(e) => set({ description: e.target.value })} />
              <p className="text-xs text-slate-500">Wrap the payoff moment in **double asterisks** to bold it. One line on why it works.</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="insp-howto">How to</Label>
              <Textarea id="insp-howto" rows={5} value={form.howTo} maxLength={INSPIRATION_HOWTO_MAX + 300} onChange={(e) => set({ howTo: e.target.value })} />
              <p className="text-xs text-slate-500">2–5 short steps, one per line.</p>
            </div>
          </div>
        </div>

        <div className="space-y-1.5">
          <Label>Adapt for</Label>
          <SelectField
            value={form.adaptFor}
            onValueChange={(v) => set({ adaptFor: v as InspirationAdaptFor })}
            options={ADAPT_FOR_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
            aria-label="Adapt for"
          />
          <p className="text-xs text-slate-500">
            For trends that have nothing to do with the client's business, choose the kind of business: the AI then says how to adapt the trend for them.
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label>Use for</Label>
            <SelectField
              value={form.usage}
              onValueChange={(v) => set({ usage: v as InspirationUsage | '' })}
              options={[{ value: '', label: 'Choose…' }, ...USAGE_OPTIONS]}
              aria-label="Use for"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Tags</Label>
            <TagChipInput value={form.tags} onChange={(tags) => set({ tags })} />
          </div>
        </div>

        <div className="space-y-2">
          <Label>Who can see it</Label>
          <AudienceRulePicker
            value={form.audience}
            onChange={(audience) => set({ audience })}
            accounts={(accounts.data ?? []) as Account[]}
            emptyHint="No audience yet — choose who can see it. An entry with no audience is shown to nobody."
          />
          {form.audience.length > 0 && (
            <div className="rounded bg-slate-50 px-2 py-1.5 text-sm" aria-live="polite">
              {reach.isPending ? (
                <span className="text-slate-500">Working out who this reaches…</span>
              ) : reach.data?.refusal ? (
                <span className="text-destructive">{reach.data.refusal}</span>
              ) : reach.data ? (
                <>
                  <span className="font-medium text-slate-900">{inspirationReachSentence(reach.data.reach)}</span>{' '}
                  <span className={reach.data.reach.accountsWithPortalAccess === 0 ? 'text-amber-700' : 'text-slate-700'}>
                    {inspirationAccessSentence(reach.data.reach)}
                  </span>
                </>
              ) : null}
            </div>
          )}
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="insp-from">Show from</Label>
            <DatePicker id="insp-from" value={form.visibleFromDate} onChange={(v) => set({ visibleFromDate: v })} ariaLabel="Show from" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="insp-until">Show until (optional)</Label>
            <DatePicker id="insp-until" value={form.visibleUntilDate} onChange={(v) => set({ visibleUntilDate: v })} ariaLabel="Show until" />
          </div>
        </div>

        <div className="rounded border border-slate-200 p-3">
          <div className="flex items-center justify-between gap-2">
            <div>
              <p className="text-sm font-medium text-slate-900">Draft with AI</p>
              <p className="text-xs text-slate-500">
                Looks at the {entry.mediaType === 'video' ? 'clip (about two frames a second)' : 'photo'} and writes the title, description, how-to and tags. Only fills fields you haven’t written.
              </p>
            </div>
            <Button type="button" variant="outline" disabled={busy || published || !asset || asset.lifecycle === 'failed'} onClick={() => draft.mutate()}>
              {draft.isPending ? <Spinner className="mr-1 h-4 w-4" /> : <Sparkles className="mr-1 h-4 w-4" />}
              {draft.isPending ? 'Drafting…' : 'Draft with AI'}
            </Button>
          </div>
          {entry.mediaType === 'video' && (
            <div className="mt-2 space-y-1">
              <Label htmlFor="insp-note">What happens / the trendLine (optional — the AI can’t hear audio)</Label>
              <Input id="insp-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder='e.g. Says “Looking for a sign?” then the card appears' />
            </div>
          )}
          {published && <p className="mt-2 text-xs text-amber-700">Unpublish to draft with AI — it never edits what clients can see.</p>}
          {draft.isPending && <p className="mt-2 text-xs text-slate-500">Reading the {entry.mediaType === 'video' ? 'frames' : 'photo'} and writing — this can take up to a minute.</p>}
          {aiMessage && <p className="mt-2 text-xs text-slate-700">{aiMessage}</p>}
        </div>

        {!published && blocker && <p className="text-xs text-amber-700">Can’t publish yet: {blocker}</p>}

        <DialogFooter className="flex-wrap gap-2 sm:justify-between">
          <div>
            {confirmDelete ? (
              <span className="flex items-center gap-2 text-sm">
                Delete this entry and its file?
                <Button size="sm" variant="destructive" disabled={busy} onClick={() => remove.mutate()}>
                  Yes, delete
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(false)}>
                  Cancel
                </Button>
              </span>
            ) : (
              <Button variant="ghost" className="text-destructive" disabled={busy} onClick={() => setConfirmDelete(true)}>
                Delete
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
            <Button variant="outline" disabled={busy || !dirty} onClick={() => save.mutate()}>
              {save.isPending ? 'Saving…' : 'Save'}
            </Button>
            {published ? (
              <Button variant="outline" disabled={busy} onClick={() => unpublish.mutate()}>
                Unpublish
              </Button>
            ) : (
              <Button disabled={busy || blocker !== null} title={blocker ?? undefined} onClick={() => publish.mutate()}>
                {publish.isPending ? 'Publishing…' : 'Publish'}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** The form's CURRENT text, shaped like the entry, so the publish blocker reads what is on screen rather than what was last saved. */
function patchPreview(f: InspirationEditForm) {
  return {
    title: f.title,
    description: f.description,
    howTo: f.howTo,
    usage: f.usage === '' ? null : f.usage,
    audience: f.audience,
  };
}

function MediaPreview({ row }: { row: StaffInspirationRow }) {
  const { entry, asset } = row;
  if (!asset || asset.lifecycle !== 'ready') {
    return (
      <div className="flex aspect-[9/16] items-center justify-center rounded bg-slate-100 p-3 text-center text-xs text-slate-500">
        {asset?.lifecycle === 'failed' ? asset.failureReason ?? 'The file failed to process.' : 'Still processing — the preview appears when it is ready.'}
      </div>
    );
  }
  if (entry.mediaType === 'video') {
    return asset.videoUrl ? (
      <video className="w-full rounded bg-black" controls muted playsInline preload="metadata" poster={asset.posterUrl ?? undefined} src={asset.videoUrl} />
    ) : (
      <div className="flex aspect-[9/16] items-center justify-center rounded bg-slate-100 text-xs text-slate-500">No preview available.</div>
    );
  }
  return asset.previewUrl ?? asset.thumbUrl ? (
    <img className="w-full rounded" src={(asset.previewUrl ?? asset.thumbUrl)!} alt={rowLabel(row)} />
  ) : (
    <div className="flex aspect-square items-center justify-center rounded bg-slate-100 text-xs text-slate-500">No preview available.</div>
  );
}
